/**
 * Browser paths a logged-out user may view without being redirected to the
 * login page. Matched by prefix (`startsWith`). Single source of truth for:
 *  - the auth-error handler (a 401 there is not an expected session end) — `services/api.ts`
 *  - the logged-out router guard (desktop + mobile returning users) — `AppRoutes.tsx`
 */
export const PUBLIC_BROWSER_PATHS = [
  "/welcome",
  "/signup",
  "/login",
  "/invite",
  "/oauth",
  // The screenshot service's sign-in bridge. The browser that opens it is
  // logged out by construction: it signs in HERE, from the custom token the AI
  // service injects. Walled, the mobile guard sent it to `/welcome` before the
  // token arrived, and every room capture became a picture of the welcome page
  // reported as a success. The page reads nothing and sets the screenshot
  // session only after a valid token signs in.
  "/screenshot-auth",
  // Legal pages must stay reachable while logged out — they are linked from
  // the welcome / login / signup screens and from within app content.
  "/terms",
  "/policy",
  // Shared JuisciGPT conversations are a public read-only acquisition surface:
  // the page itself invites anonymous readers to sign up (LoginSheet on
  // scroll) — see GPTSharedConversationPage.
  "/gpt/shared",
  // A cited-source Work landing (/work/:id) is linked from Evidence GPT answers
  // and is public read-only bibliographic data — it must be reachable logged
  // out, and the logged-out guard must not bounce it to login.
  "/work",
  // The admin's announcement visualizer frames this to preview a pop-up in a
  // chosen language. It renders one coded component against a blank page and
  // reads nothing about the viewer, so there is no session to require, and
  // nothing a logged-out visitor could learn from it that the announcement was
  // not about to tell every reader anyway.
  "/announcements/preview",
  // Shared content. A paper, a public playlist or a video story is the thing a
  // colleague sends, and the API already serves all three with no session — the
  // article page renders its full body logged out. Until these were listed, a
  // logged-out visitor who opened a shared link had the page mount, its request
  // answer 401, and the auth handler carry them to `/login`: the share was
  // dead, on mobile and in every browser where the session had lapsed.
  //
  // Listing them costs the desktop sign-in gate on these pages — it reads the
  // same list — so a logged-out desktop reader now reads the paper instead of
  // meeting the modal over it. That is the deliberate trade: the content is
  // public (`docs/room-content-visibility.md`), and a link that opens is worth
  // more than a gate on a page we were already serving in full behind it.
  "/post",
  "/playlist/public",
  // A room's resources are shareable too (decided 2026-09-24,
  // `docs/shared-content-access/README.md`). `/video` also covers
  // `/video/story`. Their pages read the content from the public API.
  "/video",
  "/infographic",
  // The same content reached through a Room. A colleague shares the paper they
  // are reading, and the link they copy carries the room they read it in.
  //
  // This opens the webapp ROUTE. The data these pages read is public by
  // decision (`router/webapp/room-access.ts`): a piece of room content opens
  // from its link. The listing of a private room stays members-only, which is
  // the distinction SEC-07 drew: the content is public, the shelf is not.
  "/room/post",
  "/room/playlist",
  // A room resource, from the room it was shared in. `/room/video` also covers
  // `/room/video/story`. A podcast's page reads the public
  // `/webapp/public/podcasts/:id/detail`.
  "/room/video",
  "/room/infographic",
  "/room/podcast",
] as const;

/**
 * Match on whole SEGMENTS, never on a bare prefix.
 *
 * `startsWith` reads `/room/playlists/:roomId` — the members-only shelf — as the
 * shared-playlist route `/room/playlist/:roomId/:id`, one character apart. That
 * would hand a private room's listing to anyone, which is the exact thing
 * `guardRoomListing` exists to prevent.
 */
export function matchesSegmentPrefix(
  pathname: string,
  prefix: string
): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Chat-with-Paper writes a conversation against the reader's account, so it
 * needs a session. It lives UNDER an article — `/post/:slug/chat` and
 * `/room/post/:roomId/:slug/chat` — so listing the article as public would
 * carry the chat along with it. Named here rather than by shaping the prefixes,
 * because the article genuinely is the public thing and the chat genuinely is
 * not.
 *
 * Matched by POSITION. The chat segment always follows the slug: index 2 under
 * `/post`, index 4 under `/room/post`, with `/chat/conversation/:id` hanging off
 * the same place. A suffix test (`endsWith("/chat")`) instead reads `/post/chat`
 * — an article whose slug happens to BE "chat" — as the chat route, and bounces
 * a logged-out reader off a paper that is public. No such slug exists today (0
 * of 34,543 locally), but slugs come from titles, so nothing stops one.
 */
function isChatWithPaper(pathname: string): boolean {
  const segments = pathname.split("/").filter((segment) => segment !== "");
  if (segments.at(0) === "post") return segments.at(2) === "chat";
  if (segments.at(0) === "room" && segments.at(1) === "post")
    return segments.at(4) === "chat";
  return false;
}

export function isPublicBrowserPath(pathname: string): boolean {
  if (isChatWithPaper(pathname)) return false;
  return PUBLIC_BROWSER_PATHS.some((p) => matchesSegmentPrefix(pathname, p));
}
